/**
 * Geometry and grouping helpers for the eBook reader.
 *
 * Coordinate model
 * ----------------
 * Annotation rects are persisted in **PDF user-space points at scale 1.0**,
 * with the origin at the top-left of the page. They are therefore independent
 * of the zoom level the reader happens to be at, of the device pixel ratio and
 * of the viewport size — which is what lets a highlight created on Android land
 * in the same place on web.
 *
 * Rendered pixels = pdfPoints * scale, so every conversion is a divide (going
 * in) or a multiply (coming out) by the current scale.
 */
import { ANNOTATION_TYPE, DEFAULT_HIGHLIGHT_HEX, HIGHLIGHT_COLOR_HEX } from '../constants/ebook';

/** Rects thinner/shorter than this (in px) are selection artefacts, not text. */
const MIN_RECT_SIZE = 1;

/** Two rects belong to the same visual line if their vertical centres are within this fraction of their height. */
const SAME_LINE_TOLERANCE = 0.5;

export const getAnnotationId = (annotation) => annotation?.annotationId ?? annotation?.id ?? null;

export const getHighlightHex = (colorName) => HIGHLIGHT_COLOR_HEX[colorName] ?? DEFAULT_HIGHLIGHT_HEX;

/**
 * Convert a viewport-relative DOMRect into page-relative PDF points.
 */
export const viewportRectToPdfRect = (rect, pageRect, scale) => ({
  left: (rect.left - pageRect.left) / scale,
  top: (rect.top - pageRect.top) / scale,
  width: rect.width / scale,
  height: rect.height / scale,
});

/**
 * Convert a stored PDF-point rect into absolute CSS pixels for the overlay.
 */
export const pdfRectToStyle = (rect, scale) => ({
  left: `${rect.left * scale}px`,
  top: `${rect.top * scale}px`,
  width: `${rect.width * scale}px`,
  height: `${rect.height * scale}px`,
});

/**
 * `Range.getClientRects()` returns one rect per text node fragment, so a single
 * selected line can come back as a dozen slivers. Merge everything that sits on
 * the same visual line into one rect spanning it.
 *
 * This is what produces the per-line highlights in the design rather than one
 * bounding box swallowing the unselected tail of the last line.
 */
export const mergeRectsIntoLines = (rects) => {
  const usable = rects.filter((rect) => rect.width > MIN_RECT_SIZE && rect.height > MIN_RECT_SIZE);
  if (usable.length === 0) return [];

  const sorted = [...usable].sort((a, b) => a.top - b.top || a.left - b.left);
  const lines = [];

  sorted.forEach((rect) => {
    const current = lines[lines.length - 1];
    const isSameLine =
      current &&
      Math.abs(rect.top + rect.height / 2 - (current.top + current.height / 2)) <
        Math.min(rect.height, current.height) * SAME_LINE_TOLERANCE;

    if (!isSameLine) {
      lines.push({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
      return;
    }

    const left = Math.min(current.left, rect.left);
    const right = Math.max(current.left + current.width, rect.left + rect.width);
    const top = Math.min(current.top, rect.top);
    const bottom = Math.max(current.top + current.height, rect.top + rect.height);

    current.left = left;
    current.top = top;
    current.width = right - left;
    current.height = bottom - top;
  });

  return lines;
};

/**
 * Turn the live DOM selection into page-bucketed PDF-space rects.
 *
 * @param {Selection} selection      window.getSelection()
 * @param {Map<number, HTMLElement>} pageElements  pageNumber -> page wrapper
 * @param {number} scale             current render scale
 * @returns {{ pageNumber: number, rects: object[], text: string } | null}
 */
export const getSelectionGeometry = (selection, pageElements, scale) => {
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null;

  const text = selection.toString().trim();
  if (!text) return null;

  const range = selection.getRangeAt(0);
  const clientRects = Array.from(range.getClientRects());
  if (clientRects.length === 0) return null;

  // A selection can physically span pages; anchor the annotation to the page
  // holding the majority of it, since the API models one pageNumber per row.
  const areaByPage = new Map();
  clientRects.forEach((rect) => {
    pageElements.forEach((element, pageNumber) => {
      const pageRect = element.getBoundingClientRect();
      const centreX = rect.left + rect.width / 2;
      const centreY = rect.top + rect.height / 2;
      const isInside =
        centreX >= pageRect.left && centreX <= pageRect.right &&
        centreY >= pageRect.top && centreY <= pageRect.bottom;

      if (isInside) {
        areaByPage.set(pageNumber, (areaByPage.get(pageNumber) ?? 0) + rect.width * rect.height);
      }
    });
  });

  if (areaByPage.size === 0) return null;

  const [pageNumber] = [...areaByPage.entries()].sort((a, b) => b[1] - a[1])[0];
  const pageElement = pageElements.get(pageNumber);
  const pageRect = pageElement.getBoundingClientRect();

  const ownRects = clientRects.filter((rect) => {
    const centreY = rect.top + rect.height / 2;
    return centreY >= pageRect.top && centreY <= pageRect.bottom;
  });

  const rects = mergeRectsIntoLines(ownRects).map((rect) => viewportRectToPdfRect(rect, pageRect, scale));
  if (rects.length === 0) return null;

  return { pageNumber, rects, text };
};

/**
 * Bounding box of a set of rects — used to position the selection popover and
 * to scroll a saved annotation back into view.
 */
export const boundingBoxOf = (rects) => {
  if (!rects?.length) return null;

  const left = Math.min(...rects.map((rect) => rect.left));
  const top = Math.min(...rects.map((rect) => rect.top));
  const right = Math.max(...rects.map((rect) => rect.left + rect.width));
  const bottom = Math.max(...rects.map((rect) => rect.top + rect.height));

  return { left, top, width: right - left, height: bottom - top };
};

/**
 * The API stores one row per rect, so a multi-line highlight arrives as several
 * rows with no group identifier. Re-assemble them client-side.
 *
 * NOTE(api): a `groupId` on the contract would make this exact rather than
 * heuristic, and would let a multi-line annotation be deleted in one call
 * instead of N. Raised with backend.
 */
export const groupAnnotations = (annotations = []) => {
  const groups = new Map();

  annotations.forEach((annotation) => {
    const type = annotation?.type === ANNOTATION_TYPE.NOTE ? ANNOTATION_TYPE.NOTE : ANNOTATION_TYPE.HIGHLIGHT;
    const key = [
      type,
      annotation?.pageNumber,
      annotation?.selectedText ?? '',
      annotation?.title ?? '',
      annotation?.note ?? '',
    ].join(' ');

    const rect = {
      left: Number(annotation?.left) || 0,
      top: Number(annotation?.top) || 0,
      width: Number(annotation?.width) || 0,
      height: Number(annotation?.height) || 0,
    };

    const id = getAnnotationId(annotation);

    const existing = groups.get(key);
    if (existing) {
      existing.rects.push(rect);
      existing.rows.push(annotation);
      if (id !== null) existing.ids.push(id);
      return;
    }

    groups.set(key, {
      key,
      type,
      pageNumber: Number(annotation?.pageNumber) || 1,
      title: annotation?.title ?? '',
      note: annotation?.note ?? '',
      color: annotation?.color,
      selectedText: annotation?.selectedText ?? '',
      rects: [rect],
      ids: id === null ? [] : [id],
      // The raw server rows. `annotations/update` wants them echoed back with
      // their annotationId, so keep them rather than reconstructing.
      rows: [annotation],
    });
  });

  return [...groups.values()].sort(
    (a, b) => a.pageNumber - b.pageNumber || (a.rects[0]?.top ?? 0) - (b.rects[0]?.top ?? 0),
  );
};

/**
 * Explode one logical annotation into the flat per-rect rows the API expects.
 */
export const toAnnotationRows = ({ type, pageNumber, title, note, color, selectedText, rects }) =>
  rects.map((rect) => {
    const row = {
      type,
      pageNumber,
      selectedText,
      color,
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };

    // Per the contract, `title` is required for notes and absent for highlights.
    if (type === ANNOTATION_TYPE.NOTE) {
      row.title = title;
      row.note = note;
    }

    return row;
  });

/**
 * Book URLs from the content API contain raw spaces, e.g.
 *   .../lesson/58470/air standard cycles basics.pdf
 * `window.open` tolerated that, but pdf.js fetches the URL, and an unencoded
 * space fails the request outright (verified: raw URL → curl exit 3, the
 * percent-encoded form → 206).
 *
 * `encodeURI` leaves an existing `%20` alone, so this is safe to apply to URLs
 * that are already encoded. Only applied when the URL actually contains
 * whitespace, so a path with a literal `%` that is not an escape sequence is
 * left untouched.
 */
export const toFetchableUrl = (url) => (typeof url === 'string' && /\s/.test(url) ? encodeURI(url) : url);

/** Case-insensitive match offsets of `query` within `text`. */
export const findMatches = (text, query) => {
  if (!text || !query) return [];

  const haystack = text.toLowerCase();
  const needle = query.toLowerCase();
  const matches = [];

  let index = haystack.indexOf(needle);
  while (index !== -1) {
    matches.push({ start: index, end: index + needle.length });
    index = haystack.indexOf(needle, index + needle.length);
  }

  return matches;
};
